import React from 'react';
import { Dialog } from '@base-ui/react/dialog';
import { cn } from '@/lib/cn';

interface LedgerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Muted supporting line under the header rule; also wires aria-describedby. */
  description?: string;
  size?: 'sm' | 'md';
  /**
   * Blocks every dismissal path (Escape, the header close button) while true.
   * Meant for "mutation in flight" states; footer buttons are consumer-owned
   * and disable themselves.
   */
  preventClose?: boolean;
  /**
   * Where focus lands when the dialog opens (Base UI pass-through: boolean,
   * ref, or function). Defaults to the first tabbable element — the header
   * close button; forms usually want their first field, confirms want Cancel.
   */
  initialFocus?: React.ComponentProps<typeof Dialog.Popup>['initialFocus'];
  footer?: React.ReactNode;
  children: React.ReactNode;
}

/*
 * Composition notes:
 * - When opening a dialog from LedgerSheet content, render it INSIDE the
 *   sheet's children. Sibling Base UI roots don't share the floating tree,
 *   so a single Escape would close both layers at once.
 * - The footer lives outside any form hosted in children. To submit from a
 *   footer button, give the form an id (`<form id="x">`) and point the button
 *   at it (`<button form="x" type="submit">`).
 */
export const LedgerDialog: React.FC<LedgerDialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  size = 'md',
  preventClose = false,
  initialFocus,
  footer,
  children,
}) => (
  <Dialog.Root
    open={open}
    // Outside presses must never dismiss the dialog: it hosts forms, and a
    // stray click would throw away whatever the user has typed.
    disablePointerDismissal
    onOpenChange={(next, eventDetails) => {
      // While preventClose holds, veto the remaining dismissal paths too:
      // Escape and any Dialog.Close press.
      if (
        preventClose &&
        (eventDetails.reason === 'escape-key' || eventDetails.reason === 'close-press')
      ) {
        eventDetails.cancel();
        return;
      }
      onOpenChange(next);
    }}
  >
    <Dialog.Portal>
      <Dialog.Backdrop
        data-print-hide
        data-testid="ledger-dialog-backdrop"
        className="fixed inset-0 z-40 bg-ink/30"
      />
      <Dialog.Popup
        data-print-hide
        initialFocus={initialFocus}
        className={cn(
          'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 flex max-h-[85dvh] w-[calc(100vw-2rem)] flex-col border border-ink bg-paper-raised p-5',
          size === 'sm' ? 'max-w-md' : 'max-w-xl',
        )}
      >
        <div className="flex items-center justify-between border-b border-ink pb-2">
          {/* cap-label bakes in the ink-muted color, so promoting the title to full ink needs the important flag */}
          <Dialog.Title className="cap-label !text-ink">{title}</Dialog.Title>
          <Dialog.Close
            aria-label="Close dialog"
            disabled={preventClose}
            className="text-ink-muted enabled:hover:text-ink disabled:opacity-40"
          >
            &#x2715;
          </Dialog.Close>
        </div>
        {description && (
          <Dialog.Description className="pt-3 text-scale-1 text-ink-muted">
            {description}
          </Dialog.Description>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto pt-4">{children}</div>
        {footer && (
          <div className="flex justify-end gap-3 border-t border-rule-faint pt-3">{footer}</div>
        )}
      </Dialog.Popup>
    </Dialog.Portal>
  </Dialog.Root>
);
